import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtBRL, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, ShieldCheck, Zap, Terminal, Ticket as TicketIcon, X, Loader2 } from "lucide-react";
import PublicNav from "@/components/Layout/PublicNav";

export default function Checkout() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [osList, setOsList] = useState([]);
  const [hostname, setHostname] = useState("");
  const [osSlug, setOsSlug] = useState("");
  const [loading, setLoading] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null); // { code, description, discount_pct }
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("pix");

  useEffect(() => {
    api.get(`/plans/${planId}`).then((r) => setPlan(r.data)).catch(() => {
      toast.error("Plano não encontrado");
      navigate("/");
    });
    api.get("/os-templates").then((r) => {
      setOsList(r.data);
      if (r.data.length && !osSlug) setOsSlug(r.data[0].slug);
    }).catch(() => setOsList([]));
    // eslint-disable-next-line
  }, [planId, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const os = osList.find((o) => o.slug === osSlug);
      const { data } = await api.post("/orders", {
        plan_id: planId,
        hostname: hostname || undefined,
        os: osSlug || (os ? os.slug : "ubuntu-22.04-x86_64"),
        coupon_code: coupon ? coupon.code : undefined,
        payment_method: paymentMethod,
      });
      toast.success(paymentMethod === "credit_card"
        ? "Pedido criado! Redirecionando para o pagamento seguro…"
        : "Pedido criado! Redirecionando para pagamento…");
      navigate(`/dashboard/invoices/${data.invoice.invoice_id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const applyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) { toast.error("Digite o código do cupom"); return; }
    setApplyingCoupon(true);
    try {
      const { data } = await api.get(`/coupons/validate?code=${encodeURIComponent(code)}`);
      setCoupon(data);
      toast.success(`Cupom ${data.code} aplicado — ${data.discount_pct}% OFF`);
    } catch (err) {
      toast.error(formatApiError(err));
      setCoupon(null);
    } finally {
      setApplyingCoupon(false);
    }
  };
  const removeCoupon = () => { setCoupon(null); setCouponInput(""); };
  const discountedTotal = plan && coupon
    ? Math.round(plan.price_brl * (1 - coupon.discount_pct / 100) * 100) / 100
    : (plan ? plan.price_brl : 0);

  if (!plan) {
    return <div className="min-h-screen flex items-center justify-center text-zinc-500">Carregando plano…</div>;
  }

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100">
      <PublicNav />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-white mb-8" data-testid="checkout-back-link">
          <ArrowLeft className="h-4 w-4" /> Voltar aos planos
        </Link>
        <div className="grid md:grid-cols-3 gap-8">
          <form onSubmit={submit} className="md:col-span-2 space-y-8" data-testid="checkout-form">
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2">// checkout</div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tighter">Configure sua VPS</h1>
            </div>

            <div className="border border-white/10 p-6 space-y-4 bg-[#121214]">
              <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">1. Configuração</div>
              <div className="space-y-2">
                <Label htmlFor="hostname" className="text-xs uppercase font-mono tracking-widest text-zinc-400">Hostname (opcional)</Label>
                <Input id="hostname" data-testid="checkout-hostname-input" placeholder="meu-servidor.dominio.com" value={hostname} onChange={(e) => setHostname(e.target.value)} className="rounded-sm bg-[#09090B] border-white/10 focus:border-white h-11" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase font-mono tracking-widest text-zinc-400">Sistema operacional</Label>
                <Select value={osSlug} onValueChange={setOsSlug}>
                  <SelectTrigger data-testid="checkout-os-select" className="rounded-sm bg-[#09090B] border-white/10 h-11"><SelectValue placeholder="Selecione…" /></SelectTrigger>
                  <SelectContent className="rounded-sm bg-[#121214] border-white/10 max-h-80">
                    {osList.map((o) => (<SelectItem key={o.slug} value={o.slug} className="font-mono text-sm">{o.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border border-white/10 p-6 space-y-4 bg-[#121214]">
              <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 flex items-center gap-2">
                <TicketIcon className="h-3.5 w-3.5" /> 2. Cupom de desconto (opcional)
              </div>
              {!coupon ? (
                <div className="flex gap-2">
                  <Input
                    data-testid="checkout-coupon-input"
                    placeholder="Ex: WELCOME20"
                    value={couponInput}
                    onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), applyCoupon())}
                    className="rounded-sm bg-[#09090B] border-white/10 focus:border-white h-11 font-mono uppercase tracking-widest"
                  />
                  <Button
                    type="button"
                    onClick={applyCoupon}
                    disabled={applyingCoupon || !couponInput.trim()}
                    variant="outline"
                    className="rounded-sm border-white/20 hover:bg-white hover:text-black h-11 px-6"
                    data-testid="checkout-coupon-apply"
                  >
                    {applyingCoupon ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aplicar"}
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 rounded-sm">
                  <div className="flex items-center gap-3">
                    <TicketIcon className="h-4 w-4 text-emerald-400" />
                    <div>
                      <div className="font-mono text-sm text-emerald-300">{coupon.code} — {coupon.discount_pct}% OFF</div>
                      {coupon.description && <div className="text-xs text-emerald-200/70">{coupon.description}</div>}
                    </div>
                  </div>
                  <button type="button" onClick={removeCoupon} className="text-emerald-300 hover:text-white" data-testid="checkout-coupon-remove">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>

            <div className="border border-white/10 p-6 space-y-4 bg-[#121214]">
              <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">3. Pagamento</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setPaymentMethod("pix")}
                  className={`border p-4 flex items-center gap-3 rounded-sm text-left transition-colors ${paymentMethod === "pix" ? "border-emerald-500/60 bg-emerald-500/5" : "border-white/10 bg-[#09090B] hover:border-white/30"}`}
                  data-testid="payment-method-pix"
                  aria-pressed={paymentMethod === "pix"}
                >
                  <div className="h-10 w-10 flex items-center justify-center border border-white/10 bg-white text-black font-black text-lg">P</div>
                  <div>
                    <div className="font-medium">PIX (Mercado Pago)</div>
                    <div className="text-xs text-zinc-500">Confirmação em segundos. QR Code após o pedido.</div>
                  </div>
                  {paymentMethod === "pix" && <div className="ml-auto text-xs font-mono text-emerald-400">●</div>}
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentMethod("credit_card")}
                  className={`border p-4 flex items-center gap-3 rounded-sm text-left transition-colors ${paymentMethod === "credit_card" ? "border-emerald-500/60 bg-emerald-500/5" : "border-white/10 bg-[#09090B] hover:border-white/30"}`}
                  data-testid="payment-method-card"
                  aria-pressed={paymentMethod === "credit_card"}
                >
                  <div className="h-10 w-10 flex items-center justify-center border border-white/10 bg-white text-black font-black text-lg">C</div>
                  <div>
                    <div className="font-medium">Cartão no Mercado Pago</div>
                    <div className="text-xs text-zinc-500">Cartão ou saldo da conta Mercado Pago, conforme disponibilidade.</div>
                  </div>
                  {paymentMethod === "credit_card" && <div className="ml-auto text-xs font-mono text-emerald-400">●</div>}
                </button>
              </div>
              {paymentMethod === "credit_card" && (
                <div className="text-xs text-zinc-500 flex items-center gap-2">
                  <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" /> Seus dados do cartão não são inseridos neste site.
                </div>
              )}
            </div>

            <Button type="submit" disabled={loading} className="rounded-sm bg-white text-black hover:bg-zinc-200 h-12 px-8 btn-sharp" data-testid="checkout-submit-btn">
              {loading ? "Processando…" : `Finalizar pedido · ${fmtBRL(discountedTotal)}`}
            </Button>
          </form>

          <aside className="border border-white/10 bg-[#121214] p-6 h-fit sticky top-24" data-testid="checkout-summary">
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">// resumo</div>
            <div className="font-heading text-2xl font-bold">{plan.name}</div>
            <p className="text-sm text-zinc-500 mt-1 mb-6">{plan.description}</p>
            <ul className="space-y-2 text-sm font-mono border-t border-white/10 pt-4">
              <li className="flex justify-between"><span className="text-zinc-500">CPU</span><span>{plan.cpu_cores} vCPU</span></li>
              <li className="flex justify-between"><span className="text-zinc-500">RAM</span><span>{plan.ram_gb} GB</span></li>
              <li className="flex justify-between"><span className="text-zinc-500">SSD</span><span>{plan.ssd_gb} GB NVMe</span></li>
              <li className="flex justify-between"><span className="text-zinc-500">Tráfego</span><span>{plan.bandwidth_tb ? `${plan.bandwidth_tb} TB` : "Ilimitado"}</span></li>
              <li className="flex justify-between"><span className="text-zinc-500">Porta</span><span>1 Gbps dedicado</span></li>
              <li className="flex justify-between"><span className="text-zinc-500">Anti-DDoS</span><span>Incluso</span></li>
              <li className="flex justify-between"><span className="text-zinc-500">Uptime SLA</span><span>99,9%</span></li>
              <li className="flex justify-between"><span className="text-zinc-500">Local</span><span>{plan.location}</span></li>
            </ul>
            <div className="border-t border-white/10 mt-6 pt-6 space-y-2">
              {coupon && (
                <>
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="text-xs text-zinc-500 font-mono uppercase tracking-widest">Subtotal</span>
                    <span className="font-mono text-zinc-500 line-through">{fmtBRL(plan.price_brl)}</span>
                  </div>
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="text-xs text-emerald-400 font-mono uppercase tracking-widest">Desconto ({coupon.code})</span>
                    <span className="font-mono text-emerald-400">−{fmtBRL(plan.price_brl - discountedTotal)}</span>
                  </div>
                </>
              )}
              <div className="flex items-baseline justify-between">
                <span className="text-xs text-zinc-500 font-mono uppercase tracking-widest">Total mensal</span>
                <span className="font-mono text-3xl font-bold">{fmtBRL(discountedTotal)}</span>
              </div>
            </div>
            <div className="mt-6 space-y-2 text-xs text-zinc-500">
              <div className="flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5" /> Anti-DDoS incluso</div>
              <div className="flex items-center gap-2"><Zap className="h-3.5 w-3.5" /> Provisionamento em 60s</div>
              <div className="flex items-center gap-2"><Terminal className="h-3.5 w-3.5" /> Root & KVM console</div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
